import { useSelector } from "react-redux";
import { Alert, Box } from "@mui/material";

import { selectCurrentAccount } from "fond/api/accountSlice";
import { useGetUserPendingInvitationsQuery } from "fond/api/userSlice";
import { useContents } from "fond/hooks/useContents";

import BlockSpinner from "../BlockSpinner";

import { AcceptInvitationPanel } from "./AcceptInvitationPanel";
import { CreateAccountPanel } from "./CreateAccountPanel";

type NoAccountContentPanelProps = {
  /**
   * Optional content to display at the top of the panel
   */
  children?: React.ReactNode;
};

/**
 * For new users with no account, we verify they don't have access to any content.
 * If they don't, we show them a panel prompting them to create an account or accept an invitation.
 *
 * If a user has an account, then this component isn't relevant.
 *
 * We only check for folders/projects if the user has no account, to avoid unnecessary API calls in places where we didn't
 * already need those things.
 */
export const NoAccountContentPanel: React.FC<NoAccountContentPanelProps> = ({ children }: NoAccountContentPanelProps) => {
  const account = useSelector(selectCurrentAccount);
  const { data: pendingInvites, isLoading: isLoadingInvites } = useGetUserPendingInvitationsQuery();
  const { isLoading: isContentLoading, items } = useContents();

  const isLoading = isContentLoading || isLoadingInvites;
  const hasPendingInvites = pendingInvites && pendingInvites?.length > 0;

  let content;
  if (account || items.length > 0) {
    return null;
  }
  if (isLoading) {
    content = <BlockSpinner />;
  } else {
    content = (
      <>
        {children}
        {hasPendingInvites ? <AcceptInvitationPanel invitations={pendingInvites} /> : <CreateAccountPanel />}
        <Alert severity="info" data-testid="no-content-alert">
          If your company already has an account, please reach out to a team member to request an invite so you can access shared content and start
          creating projects.
        </Alert>
      </>
    );
  }

  return (
    <Box my={3} data-testid="no-account-nor-content-section">
      {content}
    </Box>
  );
};
